import { useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { BA_PAIRS } from "@/data";

const Slider = ({ pair }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, next)));
  };

  return (
    <figure>
      <div
        ref={ref}
        data-testid="before-after-slider"
        className="relative h-72 overflow-hidden rounded-2xl cursor-ew-resize touch-none select-none border border-[#1E324A]"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          update(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) update(e.clientX);
        }}
      >
        <img
          src={pair.after}
          alt={pair.afterAlt}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <img
          src={pair.before}
          alt={pair.beforeAlt}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />
        {!pair.hideLabels && (
          <>
            <span className="absolute top-3 left-3 bg-[#0C1827]/85 text-white font-mono-accent text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
              Before
            </span>
            <span className="absolute top-3 right-3 bg-[#F5A623] text-[#0C1827] font-mono-accent text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full font-semibold">
              After
            </span>
          </>
        )}
        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -left-px w-0.5 bg-[#F5A623]" />
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-[#F5A623] flex items-center justify-center shadow-lg shadow-black/40">
            <MoveHorizontal size={19} className="text-[#0C1827]" />
          </div>
        </div>
      </div>
      <figcaption className="mt-4">
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#F5A623]">
          {pair.subtitle}
        </span>
        <p className="font-display font-bold text-white text-lg mt-1">{pair.title}</p>
      </figcaption>
    </figure>
  );
};

export const BeforeAfter = () => {
  return (
    <section id="transformations" className="bg-[#16283C] py-24" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5A623]">
            Transformations
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-3 uppercase">
            From Rough to Remarkable
          </h2>
          <p className="mt-4 text-base text-slate-300 leading-relaxed">
            Drag the handle on each photo to see what precision equipment and local craftsmanship
            do to raw cottage country terrain.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BA_PAIRS.map((pair) => (
            <Slider key={pair.title} pair={pair} />
          ))}
        </div>
      </div>
    </section>
  );
};
